<template>
  <BaseModal
    v-model="showModal"
    title="Modifier le client"
    size="2xl"
    :closable="!isSubmitting"
    :close-on-backdrop="!isSubmitting"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <!-- <div class="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center text-white">
          <BaseIcon name="edit" :size="20" />
        </div> -->
        <h2 class="text-lg font-semibold text-gray-900">Modifier le client</h2>
      </div>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <BaseInput
        v-model="form.name"
        label="Nom"
        placeholder="Nom du client"
        :error="errors.name"
        required
      />

      <BaseInput
        v-model="form.phone"
        label="Téléphone"
        type="tel"
        placeholder="Ex: 77 123 45 67"
        :error="errors.phone"
      />

      <BaseInput
        v-model="form.email"
        label="Email"
        type="email"
        placeholder="email@exemple.com"
        :error="errors.email"
      />

      <BaseInput
        v-model="form.address"
        label="Adresse"
        placeholder="Adresse complète"
        :error="errors.address"
      />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BaseInput
          v-model="form.city"
          label="Ville"
          placeholder="Ville"
          :error="errors.city"
        />
        <BaseInput
          v-model="form.zip"
          label="Code postal"
          placeholder="Code postal"
          :error="errors.zip"
        />
      </div>

      <BaseInput
        v-model="form.state"
        label="Région"
        placeholder="Région ou état"
        :error="errors.state"
      />
    </form>

    <template #footer>
      <div class="flex justify-end gap-3 w-full">
        <BaseButton variant="ghost" :disabled="isSubmitting" @click="closeModal">
          Annuler
        </BaseButton>
        <BaseButton
          variant="primary"
          :loading="isSubmitting"
          @click="handleSubmit"
        >
          Enregistrer
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue'
import customerService from '@/services/customerService'
import { useToastStore } from '@/store/modules/toastStore'
import { BaseModal, BaseButton, BaseInput } from '@/components/base'

export default defineComponent({
  name: 'CustomerEditModal',
  components: {
    BaseModal,
    BaseButton,
    BaseInput,
    // BaseIcon
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'updated'],
  setup(props, { emit }) {
    const toastStore = useToastStore()

    const form = ref({
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zip: ''
    })

    const errors = ref({})
    const isSubmitting = ref(false)

    const showModal = computed({
      get: () => props.show,
      set: (value) => {
        if (!value) {
          closeModal()
        }
      }
    })

    watch(() => props.customer, (newCustomer) => {
      if (newCustomer) {
        form.value = {
          name: newCustomer.name || '',
          phone: newCustomer.phone || '',
          email: newCustomer.email || '',
          address: newCustomer.address || '',
          city: newCustomer.city || '',
          state: newCustomer.state || '',
          zip: newCustomer.zip || ''
        }
        errors.value = {}
      }
    }, { immediate: true })

    watch(() => props.show, (isVisible) => {
      if (isVisible && props.customer) {
        form.value = {
          name: props.customer.name || '',
          phone: props.customer.phone || '',
          email: props.customer.email || '',
          address: props.customer.address || '',
          city: props.customer.city || '',
          state: props.customer.state || '',
          zip: props.customer.zip || ''
        }
        errors.value = {}
      }
    })

    const validateForm = () => {
      errors.value = {}
      let isValid = true

      if (!form.value.name?.trim()) {
        errors.value.name = 'Le nom est obligatoire'
        isValid = false
      }

      if (form.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        errors.value.email = 'Email invalide'
        isValid = false
      }

      return isValid
    }

    const handleSubmit = async () => {
      if (!validateForm()) return
      if (!props.customer?.id) return

      isSubmitting.value = true

      try {
        const response = await customerService.updateCustomer(props.customer.id, {
          name: form.value.name.trim(),
          phone: form.value.phone?.trim() || null,
          email: form.value.email?.trim() || null,
          address: form.value.address?.trim() || null,
          city: form.value.city?.trim() || null,
          state: form.value.state?.trim() || null,
          zip: form.value.zip?.trim() || null
        })

        toastStore.addToast({
          type: 'success',
          message: 'Client mis à jour avec succès',
          duration: 3000
        })

        emit('updated', response.data.customer || response.data)
        closeModal()
      } catch (error) {
        console.error('Erreur lors de la mise à jour:', error)

        if (error.response?.data?.errors) {
          const backendErrors = error.response.data.errors
          for (const [field, messages] of Object.entries(backendErrors)) {
            errors.value[field] = Array.isArray(messages) ? messages[0] : messages
          }
        } else {
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.message || 'Erreur lors de la mise à jour du client',
            duration: 4000
          })
        }
      } finally {
        isSubmitting.value = false
      }
    }

    const closeModal = () => {
      if (!isSubmitting.value) {
        emit('close')
      }
    }

    return {
      showModal,
      form,
      errors,
      isSubmitting,
      handleSubmit,
      closeModal
    }
  }
})
</script>
